import { Fragment, HTMLAttributes, useEffect, useState } from 'react';

import { arrayOf, bool, func, shape, string } from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';

import { CardProps } from '@/components/Atoms/Card/Card';
import ConditionalWrapper from '@/components/Atoms/ConditionalWrapper';
import { Container } from '@/components/Atoms/Container/Container';
import { NewsCard } from '@/components/Molecules/Cards/NewsCard/NewsCard';
import { SectionHeader } from '@/components/Molecules/SectionHeader/SectionHeader';
import { ArticlePropType } from '@/components/Shared/Article';
import { useDeviceType } from '@/hooks/useDeviceType';
import { Sidebar } from '@/templates/AdSidebar';
import SidebarAd from '@/templates/SidebarAd';
import getAdUnit from '@/utils/ads/getAdUnit';
import { classMerge, classNames } from '@/utils/class-names';
import { setCursor } from '@/utils/Cursor/Cursor';

import HiddenAnchor from './HiddenAnchor';

export interface PostListProps extends HTMLAttributes<HTMLHRElement> {
	/**
	 * The post list's title.
	 */
	title?: string;
	/**
	 * The post list's posts.
	 */
	posts: Post[];
	/**
	 * The post list's pagination.
	 */
	hasMorePosts?: boolean;
	/**
	 * The action when load more triggered.
	 */
	loadMore: () => void;
	/**
	 * Selected article type of the category page.
	 */
	articleType?: string;
	/**
	 * If there is a sidebar ad slot displayed.
	 */
	isSidebarDisplayed?: boolean;
	/**
	 * parent referance object if any
	 */
	parentRef?: HTMLElement | null;
	/**
	 * Posts' category
	 */
	category?: string;
	/**
	 * The level for h tags.
	 */
	level?: CardProps['level'];
	/**
	 * The cursor string of the current list
	 */
	cursorList?: string[];
	/**
	 * The length of the current list
	 */
	lengthList?: number[];
	/**
	 * The condition represents if the list is author post list or not.
	 */
	isAuthorPostList?: boolean;
	/**
	 * The prop that indicates whether the cursor is exist or not.
	 */
	isCursor?: boolean;
}

const cardStyles = classNames(
	'col-span-1 pt-5 md:pt-0',
	'border-t-4 border-neutral-grey-4 md:border-0'
);

/**
 * PostList Library Component
 */
export const PostList = ({
	className,
	title,
	posts,
	hasMorePosts,
	articleType = 'all',
	isSidebarDisplayed = false,
	parentRef = null,
	loadMore,
	level,
	category,
	isAuthorPostList,
	cursorList,
	lengthList,
	isCursor,
	...props
}: PostListProps) => {
	const [sidebarAdUnits, setSidebarAdUnits] = useState<string[]>([]);
	const deviceType = useDeviceType();

	const isDesktop = deviceType === 'lg';

	useEffect(() => {
		if ((!category || !isSidebarDisplayed) && !isDesktop) {
			return;
		}

		const adPlacements: string[] = [];

		posts.forEach((_post, index) => {
			const isAdPlacement = (index + 1) % 8 === 0;

			if (!isAdPlacement) {
				return;
			}

			const adPlacementIndex = (index + 1) / 8;

			let adPosition;

			switch (index) {
				case 0:
					adPosition = 'top';
					break;
				case posts.length - 1:
					adPosition = 'bottom';
					break;
				default:
					adPosition = 'mid';
			}

			const sidebarAdUnit = getAdUnit({
				pageType: 'category',
				environment: 'dweb',
				name: `skyscraper-${adPosition}-${adPlacementIndex}`,
			});

			adPlacements.push(sidebarAdUnit);
		});

		setSidebarAdUnits(adPlacements);
	}, [posts, category, isSidebarDisplayed, isDesktop]);

	useEffect(() => {
		if ((lengthList?.length as number) - 1 === cursorList?.length) {
			if (cursorList?.length === 0) {
				return;
			}

			const lastCursor = cursorList[cursorList.length - 1];

			setCursor(lastCursor as string);
		}
	}, [lengthList, cursorList]);

	return (
		<Container
			data-cy="PostList"
			className={classMerge(
				'px-4 lg:px-0 text-title-5 lg:text-title-3 svg-filled',
				className
			)}
			{...props}
		>
			<div
				className={classMerge(
					'col-span-full',
					isSidebarDisplayed && 'md:col-span-8'
				)}
			>
				{articleType === 'all' && title && !isCursor && (
					<SectionHeader className="mb-8">{title}</SectionHeader>
				)}
				<ConditionalWrapper
					condition={!isAuthorPostList}
					wrapper={(children) => (
						<InfiniteScroll
							loadMore={loadMore}
							pageStart={0}
							hasMore={hasMorePosts}
							useWindow={Boolean(!parentRef)}
							getScrollParent={() => parentRef}
						>
							{children}
						</InfiniteScroll>
					)}
				>
					<>
						{posts?.map((article, index) => {
							return (
								<Fragment key={article.href}>
									{lengthList?.includes(index) && (
										<>
											<HiddenAnchor
												id={cursorList?.[lengthList.indexOf(index)] as string}
												href={`${category}/${
													cursorList?.[lengthList.indexOf(index)]
												}`}
											/>
										</>
									)}
									<NewsCard
										level={level}
										className={classMerge(
											cardStyles,
											index === 3 && 'md:hidden lg:grid',
											index === 0 && 'border-t-0',
											'mb-6 md:mb-10'
										)}
										post={article}
										key={article.heading}
									/>
								</Fragment>
							);
						})}
					</>
				</ConditionalWrapper>
			</div>
			{isSidebarDisplayed && category && (
				<Sidebar as="div">
					{sidebarAdUnits.map((adUnit) => (
						<SidebarAd
							key={adUnit}
							isSticky
							adUnit={adUnit}
							className="basis-full"
						/>
					))}
				</Sidebar>
			)}
		</Container>
	);
};

PostList.propTypes = {
	title: string,
	posts: arrayOf(shape(ArticlePropType)),
	hasMorePosts: bool,
	loadMore: func,
	articleType: string,
	category: string,
};
